import { useEffect } from "react";

import { useAppStore } from "@/stores";
import { resolveCurrentCampaign } from "@/campaigns";

/**
 * Clear `selectedCampaignId` once it no longer resolves to a live campaign —
 * e.g. the selected campaign ended and dropped out of the streams. Otherwise the
 * stale id wedges the view on "Campaign Not Found" and the in-game auto-select
 * (guarded by `if (selectedId) return`) can never recover.
 *
 * Resolution goes through the campaign-type registry, so every registered type
 * counts — a per-type selector list here would silently clear selections of any
 * type it omits (and then fight the in-game auto-select in a set/clear loop).
 *
 * Gated on the `campaigns` stream having loaded so a config-set deep-link isn't
 * wiped before its data arrives. `resolves` is a boolean selector, so this
 * re-renders only when resolution actually flips, not on every stream tick.
 */
export function useClearDanglingSelection(): void {
  const selectedId = useAppStore((s) => s.selectedCampaignId);
  // Every type's view objects are assembled from the `campaigns` stream, so its
  // initial page arriving is the single readiness gate.
  const campaignsReady = useAppStore((s) => s.campaignsStream.subscribed);
  const resolves = useAppStore((s) =>
    selectedId ? resolveCurrentCampaign(s, selectedId) !== null : true
  );
  const setSelectedCampaignId = useAppStore((s) => s.setSelectedCampaignId);

  useEffect(() => {
    if (!selectedId || resolves) return;
    // Wait for the campaigns initial page before ruling an id dangling.
    if (!campaignsReady) return;
    setSelectedCampaignId(null);
  }, [selectedId, resolves, campaignsReady, setSelectedCampaignId]);
}
